import React from "react";
import injectSheet from "react-jss";
import produce from "immer";
import { cloneDeep } from "lodash";

import Selector from "./Selector";
import SelectorField from "./form-fields/SelectorField";
import { FONT_OPTS } from "../utils";
import { ExploreIconsEditor } from "./TourSettingsModal_ExploreIcons";
import { TooltipsEditor } from "./TourSettingsModal_Tooltips";
import PreviewVideoModal from "./PreviewVideoModal";
import GetVideoTutorial from "./GetVideoTutorial";
import { uploadPosterImageDataUrl } from "../services/api";

const defaultOptions = {
  vertUpperLim: 180,
  vertLowerLim: 0,
  fov: 80,
  all3D: false,
  version: 0.2,
  ui: {
    systemFont: undefined,
    menuFont: undefined,
    show: true,
    dropdownType: false,
    showFloorplanImageNames: false,
    hideFloorplans: true,
    showTimeline: false,
    enableLinksMenu: false,
    useNauticalLanguage: false,
  },
  posterPage: {
    show: true,
    image: 'url("")',
    title: "",
    defaultFont: "DaytonaPro",
    titleStyles: {
      fontSize: 64,
    },
    company: "",
    companyStyles: {
      fontSize: 25,
      textTransform: "none",
    },
    bottomText: "Powered By THEATRO 360™",
    bottomTextLink: "https://theatro360.com",
    bottomTextStyles: {
      fontSize: 22,
    },
    explore: {
      text: "",
    },
  },
  hotspotPage: {
    show: false,
    background: 'url("")',
    direction: "left",
    heightMultiplier: 1,
    externalAdjust: 0,
    newScaling: true,
  },
};
const defaultIframeOptions = cloneDeep(defaultOptions);

class SettingsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      splitOptions: true,
      iframe: false,
      options: defaultOptions,
      iframeOptions: defaultIframeOptions,
      dropdownExpanded: false,
      dropdown2Expanded: false,
      showPreviewVideo: false,
      showGetVideoTutorial: false,
      posterImageUploading: false,
    };

    this.posterImageFileInputRef = React.createRef();
    this.save = this.save.bind(this);
    this.toggleForceUppercase = this.toggleForceUppercase.bind(this);
    this.onBackgroundClick = this.onBackgroundClick.bind(this);
    this.onPosterImageFileChange = this.onPosterImageFileChange.bind(this);
  }

  onPosterImageFileChange(ev) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      window.alert("Please choose an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) {
        window.alert("Could not read the image as a data URL.");
        return;
      }

      this.setState({ posterImageUploading: true });
      uploadPosterImageDataUrl(dataUrl)
        .then(url => {
          this.onChangeOption(
            { target: { value: url } },
            "posterPage",
            "image",
          );
        })
        .catch(err => {
          console.error(err);
          window.alert(err.message || "Image upload failed.");
        })
        .finally(() => {
          this.setState({ posterImageUploading: false });
        });
    };
    reader.onerror = () => {
      window.alert("Could not read the file.");
    };
    reader.readAsDataURL(file);
  }

  componentDidMount() {
    const { tourData, tour } = this.props;
    let splitOptions = true;
    if (tourData.options) {
      const options = { ...this.state.options, ...tourData.options };
      if (options.splitOptions !== undefined) {
        splitOptions = options.splitOptions;
      }
      this.setState({ options });
      if (!splitOptions) {
        this.setState({ splitOptions: false, iframeOptions: options });
      }
    }

    if (splitOptions) {
      if (tourData.iframeOptions) {
        const iframeOptions = {
          ...this.state.iframeOptions,
          ...tourData.iframeOptions,
        };
        this.setState({ iframeOptions });
      } else {
        const stateOptions = produce(this.state.iframeOptions, draft => {
          draft.posterPage.title = tour.title;
        });
        this.setState({ iframeOptions: stateOptions, options: stateOptions });
      }
    }
  }

  onLimitChange(ev, stateEl) {
    let val = ev.target.value;
    if (val > 180) {
      val = 180;
    }
    if (val < 0) {
      val = 0;
    }

    this.save(draft => {
      draft[stateEl] = val;
    });
  }

  onChangeOption(ev, x, y, z = null) {
    let change = ev.target.value;
    if (y === "image" || y === "background") {
      change = `url("${change}")`;
    } else if (z === "fontSize") {
      change = parseInt(change);
      if (isNaN(change)) {
        return;
      }
    }

    this.save(draft => {
      if (z === null) {
        draft[x][y] = change;
      } else {
        draft[x][y][z] = change;
      }
    });
  }

  toggleSection(section, yes) {
    this.save(draft => {
      draft[section].show = yes;
    });
  }

  toggleUI(x, y) {
    this.save(draft => {
      draft.ui[x] = y;
    });
  }

  toggleForceUppercase(uppercase) {
    this.save(draft => {
      draft.posterPage.companyStyles.textTransform = uppercase;
    });
  }

  toggleHotspot(x, y) {
    this.save(draft => {
      draft.hotspotPage[x] = y;
    });
  }

  toggle3DChange() {
    this.save(draft => {
      draft.all3D = !draft.all3D;
    });
  }

  toggleSplitOption(splitOptions) {
    const { options } = this.state;
    const newOptions = produce(options, draft => {
      draft.splitOptions = splitOptions;
    });

    this.props.onUpdateTourOptions(newOptions, newOptions);
    this.setState({
      splitOptions,
      options: newOptions,
      iframeOptions: newOptions,
      iframe: false,
    });
  }

  toggleIframe(iframe) {
    this.setState({ iframe });
  }

  save(draft) {
    const {
      options: oldOptions,
      iframeOptions: oldIframeOptions,
      iframe,
      splitOptions,
    } = this.state;
    const newOptions = produce(oldOptions, draft);
    const newIframeOptions = produce(oldIframeOptions, draft);
    let options = oldOptions;
    let iframeOptions = oldIframeOptions;
    if (splitOptions) {
      if (iframe) {
        iframeOptions = newIframeOptions;
      } else {
        options = newOptions;
      }
    } else {
      options = newOptions;
      iframeOptions = newOptions;
    }
    this.props.onUpdateTourOptions(options, iframeOptions);
    this.setState({ options, iframeOptions });
  }

  onBackgroundClick() {
    const confirmText =
      "You can close this window but your changes will not be saved until you save the tour";
    if (window.confirm(confirmText)) this.props.toggleTourSettings();
  }

  render() {
    const { classes, toggleTourSettings, togglePublishPopup } = this.props;
    const {
      options,
      iframeOptions,
      splitOptions,
      iframe,
      posterImageUploading,
    } = this.state;

    const currentOpts = splitOptions && iframe ? iframeOptions : options;

    return (
      <div>
        <div className={classes.centerer}>
          <div
            className={classes.backgroundHider}
            onClick={this.onBackgroundClick}
          />
          <div className={classes.container}>
            <div className={classes.close} onClick={toggleTourSettings} />
            <section className={classes.section}>
              <h1 style={{ marginBottom: "0px" }}>Settings Menu</h1>
              <p className={classes.infoText}>
                Control how your tour is displayed to viewers
              </p>

              <hr className={classes.breakingLine} />

              <h2>General Settings</h2>
              <h3>Camera settings</h3>
              <div className={classes.settingsItem}>
                <label>Camera Field of view (degrees):</label>
                <input
                  value={currentOpts.fov}
                  onChange={ev => {
                    this.onLimitChange(ev, "fov");
                  }}
                />
              </div>
              <div className={classes.settingsItem}>
                <label>Vertical upper limit (degrees):</label>
                <input
                  value={currentOpts.vertUpperLim}
                  onChange={ev => {
                    this.onLimitChange(ev, "vertUpperLim");
                  }}
                />
              </div>
              <div className={classes.settingsItem}>
                <label>Vertical lower limit (degrees):</label>
                <input
                  value={currentOpts.vertLowerLim}
                  onChange={ev => {
                    this.onLimitChange(ev, "vertLowerLim");
                  }}
                />
              </div>

              <h3>3D Stereoscopy</h3>
              <button
                className={currentOpts.all3D ? classes.buttonSelected : ""}
                onClick={() => {
                  this.toggle3DChange();
                }}
              >
                All images are 3D
              </button>

              <hr className={classes.breakingLine} />

              <h2>Publishing Settings</h2>

              <div className={classes.settingsItem}>
                <label>
                  Tour visible on:{" "}
                  {options.publish
                    ? options.publish.length > 0
                      ? options.publish.map((sub, i) => (
                        <span className={classes.sub} key={sub}>
                          {i < options.publish.length && i !== 0 && ", "}
                          {sub}
                        </span>
                      ))
                      : "No subdomains (unpublished)"
                    : "All subdomains"}
                </label>
                <button
                  onClick={() =>
                    void (toggleTourSettings(), togglePublishPopup())
                  }
                >
                  Change
                </button>
              </div>

              <hr className={classes.breakingLine} />

              <h2>Tour Settings</h2>

              <div className={classes.settingsItem}>
                <div>
                  <label>Separate iFrame options:</label>
                  <p className={classes.infoText}>
                    Show a different set of settings for when viewing a tour in
                    an iFrame
                  </p>
                </div>
                <div className={classes.buttonsCont}>
                  <button
                    className={splitOptions ? classes.buttonSelected : ""}
                    onClick={() => this.toggleSplitOption(true)}
                  >
                    Enable
                  </button>
                  <button
                    className={!splitOptions ? classes.buttonSelected : ""}
                    onClick={() => this.toggleSplitOption(false)}
                  >
                    Disable
                  </button>
                </div>
              </div>

              {splitOptions && (
                <div className={classes.settingsItem}>
                  <div style={{ display: "flex", width: "100%" }}>
                    <div
                      className={
                        !iframe
                          ? classes.menuSelectorSelected
                          : classes.menuSelector
                      }
                      onClick={() => this.toggleIframe(false)}
                    >
                      TOUR OPTIONS
                    </div>
                    <div
                      className={
                        iframe
                          ? classes.menuSelectorSelected
                          : classes.menuSelector
                      }
                      onClick={() => this.toggleIframe(true)}
                    >
                      IFRAME OPTIONS
                    </div>
                  </div>
                </div>
              )}

              <div className={classes.settingsItem}>
                <div>
                  <label>Crossfade transition speed:</label>
                  <p className={classes.infoText}>
                    Scale up / down transition speed. Must be number. Default value is 1
                  </p>
                </div>
                <input
                  value={currentOpts.ui.crossfadeTransitionSpeed || ""}
                  onChange={ev =>
                    this.onChangeOption(ev, "ui", "crossfadeTransitionSpeed")
                  }
                />
              </div>

              <div className={classes.settingsItem}>
                <div>
                  <label>Autoplay speed:</label>
                  <p className={classes.infoText}>
                    Time to complete an autoplay round. The higher the number (seconds), the slower the autoplay. Default value is 10 (seconds)
                  </p>
                </div>
                <input
                  value={currentOpts.ui.autoplaySpeed || ""}
                  onChange={ev =>
                    this.onChangeOption(ev, "ui", "autoplaySpeed")
                  }
                />
              </div>

              <h3>UI Options</h3>

              <div>
                <SelectorField
                  label="System Font"
                  description="Apply to all page. If any other font options below are set, it will overide this config"
                  value={currentOpts.ui?.systemFont}
                  options={FONT_OPTS}
                  onChange={ev =>
                    this.onChangeOption(
                      { target: { value: ev } },
                      "ui",
                      "systemFont",
                    )
                  }
                />
                <SelectorField
                  label="Menu Font"
                  description="Apply to Images Dropdown Menu"
                  value={currentOpts.ui?.menuFont}
                  options={FONT_OPTS}
                  onChange={ev =>
                    this.onChangeOption(
                      { target: { value: ev } },
                      "ui",
                      "menuFont",
                    )
                  }
                />

                <div className={classes.settingsItem}>
                  <div>
                    <label>Show splitscreen button:</label>
                    <p className={classes.infoText}>
                      Tour: Show the splitscreen button in all scenes
                      <br />
                      Solo: Show the splitscreen button only when a scene has
                      been manually linked
                    </p>
                  </div>
                  <div className={classes.buttonsCont}>
                    <button
                      className={
                        currentOpts.ui.showTimeline
                          ? classes.buttonSelected
                          : ""
                      }
                      onClick={() => this.toggleUI("showTimeline", true)}
                    >
                      Tour
                    </button>
                    <button
                      className={
                        !currentOpts.ui.showTimeline
                          ? classes.buttonSelected
                          : ""
                      }
                      onClick={() => this.toggleUI("showTimeline", false)}
                    >
                      Solo
                    </button>
                  </div>
                </div>
                <div className={classes.settingsItem}>
                  <div>
                    <label>Show Floorplan:</label>
                    <p className={classes.infoText}>
                      Show floorplans you have added
                    </p>
                  </div>
                  <div className={classes.buttonsCont}>
                    <button
                      className={
                        !currentOpts.ui.hideFloorplans
                          ? classes.buttonSelected
                          : ""
                      }
                      onClick={() => this.toggleUI("hideFloorplans", false)}
                    >
                      Show
                    </button>
                    <button
                      className={
                        currentOpts.ui.hideFloorplans
                          ? classes.buttonSelected
                          : ""
                      }
                      onClick={() => this.toggleUI("hideFloorplans", true)}
                    >
                      Hide
                    </button>
                  </div>
                </div>
                {/* <div className={classes.settingsItem}>
                  <label>Show Floorplan Image Names:</label>
                  <div>
                    <button className={currentOpts.ui.showFloorplanImageNames ? classes.buttonSelected : ''} onClick={() => this.toggleUI('showFloorplanImageNames', true)}>Show</button>
                    <button className={!currentOpts.ui.showFloorplanImageNames ? classes.buttonSelected : ''} onClick={() => this.toggleUI('showFloorplanImageNames', false)}>Hide</button>
                  </div>
                </div> */}
                <div className={classes.settingsItem}>
                  <div>
                    <label>Dropdown Type:</label>
                    <p className={classes.infoText}>
                      Show a dropdown with the names of boat decks on it
                    </p>
                  </div>
                  <div className={classes.dropdown}>
                    <Selector
                      isTeamSelect
                      noMaxWidth
                      expanded={this.state.dropdownExpanded}
                      toggle={() =>
                        this.setState({
                          dropdownExpanded: !this.state.dropdownExpanded,
                        })
                      }
                      onChange={value => this.toggleUI("dropdownType", value)}
                      value={currentOpts.ui.dropdownType}
                      options={[
                        {
                          label: "None",
                          value: false,
                        },
                        {
                          label: "Images List",
                          value: "list",
                        },
                        {
                          label: "Floorplans",
                          value: "floorplans",
                        },
                        {
                          label: "Floorplans with List",
                          value: "floorplansWithList",
                        },
                      ]}
                    />
                  </div>
                </div>
                {currentOpts.ui.dropdownType && (
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Dropdown Title Override:</label>
                      <p className={classes.infoText}>
                        Override the tour title in the dropdown. Leave blank to
                        just use the tour name
                      </p>
                    </div>
                    <input
                      value={currentOpts.ui.dropdownTitle || ""}
                      onChange={ev =>
                        this.onChangeOption(ev, "ui", "dropdownTitle")
                      }
                    />
                  </div>
                )}
                <div className={classes.settingsItem}>
                  <div>
                    <label>Show Listings:</label>
                    <p className={classes.infoText}>
                      Show a dropdown with the names of images on your
                      floorplans
                    </p>
                  </div>
                  <div className={classes.buttonsCont}>
                    <button
                      className={
                        currentOpts.ui.showListings
                          ? classes.buttonSelected
                          : ""
                      }
                      onClick={() => this.toggleUI("showListings", true)}
                    >
                      Show
                    </button>
                    <button
                      className={
                        !currentOpts.ui.showListings
                          ? classes.buttonSelected
                          : ""
                      }
                      onClick={() => this.toggleUI("showListings", false)}
                    >
                      Hide
                    </button>
                  </div>
                </div>
                {currentOpts.ui.dropdownType && (
                  <>
                    <div className={classes.settingsItem}>
                      <div>
                        <label>Links:</label>
                        <p className={classes.infoText}>
                          Show a list of Links in the dropdown
                        </p>
                      </div>
                      <div className={classes.buttonsCont}>
                        <button
                          className={
                            currentOpts.ui.enableLinksMenu
                              ? classes.buttonSelected
                              : ""
                          }
                          onClick={() => this.toggleUI("enableLinksMenu", true)}
                        >
                          Show
                        </button>
                        <button
                          className={
                            !currentOpts.ui.enableLinksMenu
                              ? classes.buttonSelected
                              : ""
                          }
                          onClick={() =>
                            this.toggleUI("enableLinksMenu", false)
                          }
                        >
                          Hide
                        </button>
                      </div>
                    </div>
                    {currentOpts.ui.enableLinksMenu && (
                      <>
                        <div className={classes.settingsItem}>
                          <div>
                            <label>Links toggle language:</label>
                            <p className={classes.infoText}>
                              Use "Scenes" or "Decks" in the Links menu toggle
                            </p>
                          </div>
                          <div className={classes.buttonsCont}>
                            <button
                              className={
                                !currentOpts.ui.useNauticalLanguage
                                  ? classes.buttonSelected
                                  : ""
                              }
                              onClick={() =>
                                this.toggleUI("useNauticalLanguage", false)
                              }
                            >
                              Scenes
                            </button>
                            <button
                              className={
                                currentOpts.ui.useNauticalLanguage
                                  ? classes.buttonSelected
                                  : ""
                              }
                              onClick={() =>
                                this.toggleUI("useNauticalLanguage", true)
                              }
                            >
                              Decks
                            </button>
                          </div>
                        </div>
                        <div className={classes.settingsItem}>
                          <div>
                            <label>Custom Menu Label:</label>
                            <p className={classes.infoText}>
                              Override the menu label for the Links menu
                            </p>
                          </div>
                          <input
                            value={currentOpts.ui.linksOverride ?? "LINKS"}
                            onChange={ev =>
                              this.onChangeOption(ev, "ui", "linksOverride")
                            }
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <h3>Poster Page</h3>
              <div className={classes.settingsItem}>
                <div>
                  <label>Show Poster Page:</label>
                  <p className={classes.infoText}>
                    Show a poster page with your text on it
                  </p>
                </div>
                <div className={classes.buttonsCont}>
                  <button
                    className={
                      currentOpts.posterPage.show ? classes.buttonSelected : ""
                    }
                    onClick={() => this.toggleSection("posterPage", true)}
                  >
                    Yes
                  </button>
                  <button
                    className={
                      !currentOpts.posterPage.show ? classes.buttonSelected : ""
                    }
                    onClick={() => this.toggleSection("posterPage", false)}
                  >
                    No
                  </button>
                </div>
              </div>

              {currentOpts.posterPage.show && (
                <div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Poster Title:</label>
                      <p className={classes.infoText}>
                        Displayed at the top of your poster page e.g My Awesome
                        Tour
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.title}
                      onChange={ev =>
                        this.onChangeOption(ev, "posterPage", "title")
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Title Font:</label>
                      <p className={classes.infoText}>
                        Choose the font used by the Poster Page title
                      </p>
                    </div>
                    <div className={classes.dropdown}>
                      <Selector
                        isTeamSelect
                        noMaxWidth
                        expanded={this.state.dropdown2Expanded}
                        toggle={() =>
                          this.setState({
                            dropdown2Expanded: !this.state.dropdown2Expanded,
                          })
                        }
                        onChange={ev =>
                          this.onChangeOption(
                            { target: { value: ev } },
                            "posterPage",
                            "defaultFont",
                          )
                        }
                        value={currentOpts.posterPage.defaultFont}
                        options={[
                          {
                            label: "Daytona Pro",
                            value: "DaytonaPro",
                          },
                          {
                            label: "Montserrat",
                            value: "Montserrat",
                          },
                        ]}
                      />
                    </div>
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Title Font Size:</label>
                      <p className={classes.infoText}>
                        How large to display your text - measured in pixels
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.titleStyles.fontSize}
                      onChange={ev =>
                        this.onChangeOption(
                          ev,
                          "posterPage",
                          "titleStyles",
                          "fontSize",
                        )
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Poster Image URL:</label>
                      <p className={classes.infoText}>
                        Display an image behind your poster page
                      </p>
                    </div>
                    <div style={{ width: "50%" }}>
                      <input
                        type="file"
                        ref={this.posterImageFileInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={this.onPosterImageFileChange}
                      />
                      <input
                        value={(splitOptions && iframe
                          ? iframeOptions
                          : options
                        ).posterPage.image.slice(5, -2)}
                        style={{
                          width: "calc(100% - 110px)",
                          marginLeft: 10,
                        }}
                        disabled={posterImageUploading}
                        onChange={ev =>
                          this.onChangeOption(ev, "posterPage", "image")
                        }
                      />
                      <button
                        type="button"
                        className={classes.buttonSelected}
                        style={{ height: 32, width: 70, marginTop: 3 }}
                        disabled={posterImageUploading}
                        onClick={() =>
                          this.posterImageFileInputRef.current?.click()
                        }
                      >
                        {posterImageUploading ? "....." : "Upload"}
                      </button>
                    </div>
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Poster Video URL:</label>
                      <p className={classes.infoText}>
                        Display a video behind your poster page
                      </p>
                    </div>
                    <div style={{ width: "50%" }}>
                      <input
                        value={currentOpts.posterPage.video}
                        style={{ width: "calc(100% - 110px)", marginLeft: 10 }}
                        onChange={ev =>
                          this.onChangeOption(ev, "posterPage", "video")
                        }
                      />
                      <button
                        className={classes.buttonSelected}
                        style={{ height: 32, width: 70, marginTop: 3 }}
                        onClick={() => {
                          this.setState({ showPreviewVideo: true })
                        }}
                      >
                        Preview
                      </button>
                      <p className={classes.linkText} onClick={() => this.setState({ showGetVideoTutorial: true })}>
                        How to get video url?
                      </p>
                    </div>
                    {this.state.showPreviewVideo && (
                      <PreviewVideoModal
                        onClose={() => this.setState({ showPreviewVideo: false })}
                        url={currentOpts.posterPage.video}
                      />
                    )}
                    {this.state.showGetVideoTutorial && (
                      <GetVideoTutorial
                        onClose={() => this.setState({ showGetVideoTutorial: false })}
                      />
                    )}
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Subtitle:</label>
                      <p className={classes.infoText}>
                        Displayed just below your title
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.company}
                      onChange={ev =>
                        this.onChangeOption(ev, "posterPage", "company")
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Subtitle Font Size:</label>
                      <p className={classes.infoText}>
                        How large to display your text - measured in pixels
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.companyStyles.fontSize}
                      onChange={ev =>
                        this.onChangeOption(
                          ev,
                          "posterPage",
                          "companyStyles",
                          "fontSize",
                        )
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Subtitle Force Uppercase:</label>
                      <p className={classes.infoText}>
                        Force the subtitle text to be all uppercase
                      </p>
                    </div>
                    <div className={classes.buttonsCont}>
                      <button
                        className={
                          currentOpts.posterPage.companyStyles.textTransform ===
                            "uppercase"
                            ? classes.buttonSelected
                            : ""
                        }
                        onClick={() => this.toggleForceUppercase("uppercase")}
                      >
                        Enable
                      </button>
                      <button
                        className={
                          currentOpts.posterPage.companyStyles.textTransform ===
                            "none"
                            ? classes.buttonSelected
                            : ""
                        }
                        onClick={() => this.toggleForceUppercase("none")}
                      >
                        Disable
                      </button>
                    </div>
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Attribution:</label>
                      <p className={classes.infoText}>
                        Displayed just under the play button, can link to your
                        website
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.bottomText}
                      onChange={ev =>
                        this.onChangeOption(ev, "posterPage", "bottomText")
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Attribution URL:</label>
                      <p className={classes.infoText}>
                        Where to link your attribution to - leave blank for
                        nowhere
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.bottomTextLink}
                      onChange={ev =>
                        this.onChangeOption(ev, "posterPage", "bottomTextLink")
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Bottom Text Font Size:</label>
                      <p className={classes.infoText}>
                        How large to display your text - measured in pixels
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.bottomTextStyles.fontSize}
                      onChange={ev =>
                        this.onChangeOption(
                          ev,
                          "posterPage",
                          "bottomTextStyles",
                          "fontSize",
                        )
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Explore text:</label>
                      <p className={classes.infoText}>
                        Text for the info menu at the bottom of the page - leave
                        blank to remove menu
                      </p>
                    </div>
                    <input
                      value={currentOpts.posterPage.explore.text}
                      onChange={ev =>
                        this.onChangeOption(ev, "posterPage", "explore", "text")
                      }
                    />
                  </div>
                </div>
              )}

              <h3>Explore icon labels</h3>
              <p className={classes.infoText}>
                Customize the label of explore icons showing on PosterPage
              </p>
              <ExploreIconsEditor
                value={currentOpts.posterPage.explore}
                onChange={(icon, ev) =>
                  this.onChangeOption(ev, "posterPage", "explore", icon)
                }
              />

              <h3>Tooltips</h3>
              <p className={classes.infoText}>
                Customize the content of tooltips
              </p>
              <TooltipsEditor
                value={currentOpts.ui.tooltips}
                onChange={(ev) =>
                  this.onChangeOption(ev, "ui", "tooltips")
                }
              />

              {/* <h3>Hotspot Page</h3>
              <p className={classes.infoText}></p>
              <div className={classes.settingsItem}>
                <label>Show hotspot page:</label>
                <div className={classes.buttonsCont}>
                  <button
                    className={
                      currentOpts
                        .hotspotPage.show
                        ? classes.buttonSelected
                        : ""
                    }
                    onClick={() => this.toggleSection("hotspotPage", true)}
                  >
                    Yes
                  </button>
                  <button
                    className={
                      !currentOpts
                        .hotspotPage.show
                        ? classes.buttonSelected
                        : ""
                    }
                    onClick={() => this.toggleSection("hotspotPage", false)}
                  >
                    No
                  </button>
                </div>
              </div>

              {currentOpts.hotspotPage
                .show && (
                <div>
                  <div className={classes.settingsItem}>
                    <label>Background Image URL:</label>
                    <input
                      value={(splitOptions && iframe
                        ? iframeOptions
                        : options
                      ).hotspotPage.background.slice(5, -2)}
                      onChange={ev =>
                        this.onChangeOption(ev, "hotspotPage", "background")
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <div>
                      <label>Direction:</label>
                      <p className={classes.infoText}>
                        This setting also affects {iframe ? "tour " : "iframe "}
                        options
                      </p>
                    </div>
                    <div className={classes.buttonsCont}>
                      <button
                        className={
                          currentOpts
                            .hotspotPage.direction === "left"
                            ? classes.buttonSelected
                            : ""
                        }
                        onClick={() => this.toggleHotspot("direction", "left")}
                      >
                        Left to Right
                      </button>
                      <button
                        className={
                          currentOpts
                            .hotspotPage.direction === "right"
                            ? classes.buttonSelected
                            : ""
                        }
                        onClick={() => this.toggleHotspot("direction", "right")}
                      >
                        Right to Left
                      </button>
                    </div>
                  </div>
                  <div className={classes.settingsItem}>
                    <label>Image Height Multiplier:</label>
                    <input
                      value={
                        currentOpts
                          .hotspotPage.heightMultiplier
                      }
                      onChange={ev =>
                        this.onChangeOption(
                          ev,
                          "hotspotPage",
                          "heightMultiplier",
                        )
                      }
                    />
                  </div>
                  <div className={classes.settingsItem}>
                    <label>External Image Adjustment:</label>
                    <input
                      value={
                        currentOpts
                          .hotspotPage.externalAdjust
                      }
                      onChange={ev =>
                        this.onChangeOption(ev, "hotspotPage", "externalAdjust")
                      }
                    />
                  </div>
                </div>
              )} */}
              <div className={classes.end} />
            </section>
          </div>
        </div>
      </div>
    );
  }
}
const styles = theme => ({
  "@keyframes fadein": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  container: {
    width: 800,
    maxWidth: "calc(100% - 40px)",
    height: "70vh",
    background: theme.colorDarkGrey,
    zIndex: 1000,
    boxShadow: "2px 0px 16px rgba(0, 0, 0, 0.27)",
    "border-radius": 3,
    color: "white",
    animation: "$fadein ease 0.2s",
    position: "relative",
    "& h2": {
      color: theme.colorSecondary,
    },
    "& button": {
      background: "#575757",
      color: "white",
      outline: "none",
      border: "none",
      "border-radius": 3,
      padding: "4px 15px",
      marginLeft: 5,
      cursor: "pointer",
    },
  },
  settingsItem: {
    marginTop: 10,
    marginBottom: 5,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    "& input": {
      marginLeft: 30,
      height: 30,
      flexShrink: 0,
      width: "50%",
      maxWidth: 300,
      outline: "none",
      "border-radius": 4,
      padding: "3px 9px",
      fontSize: 15,
      border: "1px solid transparent",
      background: theme.colorBackground,
      color: "white",
      fontFamily: "DaytonaPro-Light",
      "&:focus": {
        border: `1px solid ${theme.colorPrimary} !important`,
      },
    },
  },
  centerer: {
    width: "100%",
    height: "100%",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 999,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  backgroundHider: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  buttonSelected: {
    background: `${theme.colorSecondary} !important`,
  },
  breakingLine: {
    border: "1px solid #575757",
    marginTop: "41px",
    marginBottom: "41px",
  },
  menuSelector: {
    padding: 5,
    transition: "all 0.2s",
    paddingLeft: 25,
    paddingRight: 25,
    background: "#575757",
    cursor: "pointer",
    flex: 1,
    textAlign: "center",
  },
  menuSelectorSelected: {
    extend: "menuSelector",
    background: `${theme.colorSecondary} !important`,
  },
  infoText: {
    marginTop: "5px",
    color: "#9e9e9e",
    marginBottom: "5px",
    maxWidth: 300,
  },
  buttonsCont: {
    alignItems: "center",
    display: "flex",
  },
  section: {
    overflowY: "auto",
    padding: [40, 70],
    height: "calc(100% - 80px)",
  },
  end: {
    height: 60,
  },
  close: {
    position: "absolute",
    left: -16,
    top: -16,
    zIndex: 1001,
    cursor: "pointer",
    "background-image": "url(/assets/close-hover.png)",
    "background-size": "contain",
    width: 32,
    height: 32,
    "border-radius": 16,
    animation: "$fadein ease 0.2s",
  },
  dropdown: {
    marginLeft: 30,
    width: "50%",
    position: "relative",
    height: 29,
    display: "flex",
  },
  sub: {
    "font-family": "monospace",
  },
  warningBox: {
    backgroundColor: "#ff9900",
    padding: 15,
    borderRadius: 5,
    marginTop: 15,
    "& h2": {
      color: "black",
      marginTop: 0,
      marginBottom: 10,
    },
    "& p": {
      color: "black",
      margin: 0,
    },
  },
  warningIcon: {
    float: "left",
    marginRight: 15,
    fontSize: 60,
  },
  linkText: {
    color: theme.colorSecondary,
    cursor: "pointer",
    textAlign: "right",
    textDecoration: "underline",
  }
});

export default injectSheet(styles)(SettingsModal);
export { defaultOptions, defaultIframeOptions };
